<template>
  <b-card>
    <b-button variant="link" class="p-0 mb-1" @click="goBack">
      <feather-icon icon="ArrowLeftIcon" size="16" /> Volver a Beach Club
    </b-button>

    <div v-if="isLoading && !booking" class="text-center my-3">
      <b-spinner class="align-middle" /> <strong>Cargando reserva</strong>
    </div>

    <b-alert v-else-if="!booking" show variant="warning" class="p-1">No se encontró la reserva.</b-alert>

    <template v-else>
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
        <h3 class="mb-0">
          Reserva {{ booking.folio }}
          <b-badge pill :class="'bg-' + statusVariant(booking.status)" class="ml-50 align-middle">{{ booking.statusname }}</b-badge>
        </h3>
        <div class="mt-1 mt-md-0">
          <b-button v-if="canEdit" variant="outline-primary" size="sm" class="mr-50" :disabled="isSaving" @click="editModal = { visible: true }">
            <feather-icon icon="EditIcon" size="14" /> Editar
          </b-button>
          <b-button v-if="canConfirm" variant="success" size="sm" class="mr-50" :disabled="isSaving" @click="openStatusModal('confirm')">
            <feather-icon icon="CheckCircleIcon" size="14" /> Confirmar
          </b-button>
          <b-button v-if="canCancel" variant="outline-danger" size="sm" :disabled="isSaving" @click="openStatusModal('cancel')">
            <feather-icon icon="XCircleIcon" size="14" /> Cancelar
          </b-button>
          <b-button v-if="canReviewCancel" variant="danger" size="sm" class="mr-50" :disabled="isSaving" @click="openStatusModal('approveCancel')">
            <feather-icon icon="CheckCircleIcon" size="14" /> Aprobar cancelación
          </b-button>
          <b-button v-if="canReviewCancel" variant="outline-primary" size="sm" :disabled="isSaving" @click="openStatusModal('rejectCancel')">
            <feather-icon icon="RotateCcwIcon" size="14" /> Rechazar cancelación
          </b-button>
        </div>
      </div>

      <section v-for="section in sections" :key="section.title" class="mb-2">
        <h5 class="border-bottom pb-50 mb-1">{{ section.title }}</h5>
        <b-row>
          <div v-for="(field, index) in section.fields" :key="index" :class="field.wide ? 'col-12' : 'col-6 col-md-4 col-lg-2'" class="mb-1">
            <span v-if="field.label" class="d-block font-weight-bolder">{{ field.label }}</span>
            <span :class="{ notes: field.wide }">{{ displayValue(field.value) }}</span>
          </div>
        </b-row>
      </section>
    </template>

    <ModalOwnerBookingStatus
      :visible="statusModal.visible"
      :mode="statusModal.mode"
      :modes="statusModes"
      :booking="booking"
      :isSaving="isSaving"
      @submit="submitStatus"
      @close="closeStatusModal"
    >
      <template #summary>
        <p class="mb-1"><b>Fecha de visita:</b> {{ formatDay(booking.visitDate) }}</p>
        <p class="mb-2"><b>Adultos / Menores / Infantes:</b> {{ booking.adults }} / {{ booking.children }} / {{ booking.infants }}</p>
      </template>
    </ModalOwnerBookingStatus>

    <ModalEditBeachClubBooking
      :visible="editModal.visible"
      :booking="booking"
      :isSaving="isSaving"
      @save="saveBooking"
      @close="closeEditModal"
    />
  </b-card>
</template>

<script>
import { mapActions } from 'vuex'
import { formatXcalacocoDay, formatXcalacocoDateTime, xcalacocoStatusVariant } from '@/helpers/xcalacocoBookingsHelper'
import { formatBeachClubMoney } from '@/helpers/beachClubBookingsHelper'
import { ownerBookingDetail } from '@/modules/fivesClub/mixins/ownerBookingDetail'
import ModalOwnerBookingStatus from '@/modules/fivesClub/components/catalogs/ownersBookings/ModalOwnerBookingStatus'
import ModalEditBeachClubBooking from '@/modules/fivesClub/components/catalogs/ownersBookings/beachClub/ModalEditBeachClubBooking'

const CANCELLED = '3'

export default {
  mixins: [ownerBookingDetail],
  components: {
    ModalOwnerBookingStatus,
    ModalEditBeachClubBooking,
  },
  data() {
    return {
      listTab: 'beachclub',
      // No locator: there is no PMS behind the Beach Club
      confirmMode: {
        title: 'Confirmar reserva',
        hint: 'La reserva quedará Confirmada. El cupo del Beach Club se controla a mano; revísalo antes de confirmar.',
        button: 'Confirmar',
        variant: 'primary',
        body: booking => ({ id: booking.id, status: '2', stamp: booking.stamp }),
        success: 'Reserva confirmada',
      },
      actionPermissions: {
        edit: 'fivesclub_owner_booking_edit_beach_club_booking',
        confirm: 'fivesclub_owner_booking_confirm_beach_club_booking',
        cancel: 'fivesclub_owner_booking_cancel_beach_club_booking',
      },
    }
  },
  computed: {
    sections() {
      const { booking } = this
      if (!booking) return []
      return [
        {
          title: 'Identificadores',
          fields: [
            { label: 'Confirmación', value: booking.folio },
            { label: 'Solicitada el', value: this.formatDateTime(booking.createDate) },
            { label: 'Reglamento aceptado', value: booking.rulesAccepted ? 'Sí' : 'No' },
          ],
        },
        {
          title: 'Visita',
          fields: [
            { label: 'Vivienda', value: booking.housingnumber },
            { label: 'Propietario', value: booking.ownerName },
            { label: 'Fecha de visita', value: this.formatDay(booking.visitDate) },
          ],
        },
        {
          title: 'Personas',
          fields: [
            { label: 'Adultos', value: booking.adults },
            { label: 'Menores', value: booking.children },
            { label: 'Infantes', value: booking.infants },
            { label: 'Titular', value: `${booking.holderName || ''} ${booking.holderLastname || ''}`.trim() },
          ],
        },
        {
          title: 'Cotización',
          fields: [
            { label: 'Membresía activa', value: booking.membershipActive ? 'Sí' : 'No' },
            { label: 'Recámaras', value: booking.bedrooms },
            { label: 'Adultos cubiertos', value: booking.coveredAdults },
            { label: 'Menores cubiertos', value: booking.coveredChildren },
            { label: 'Adultos extra', value: booking.extraAdults },
            { label: 'Menores extra', value: booking.extraChildren },
            { label: 'Tarifa adulto', value: this.formatMoney(booking.adultRate) },
            { label: 'Tarifa menor', value: this.formatMoney(booking.childRate) },
            { label: 'Total a pagar en la entrada', value: this.formatMoney(booking.total) },
          ],
        },
      ].concat(this.cancelRequestSection(booking), this.cancelSection(booking))
    },
  },
  created() {
    this.loadBooking()
  },
  methods: {
    ...mapActions('fivesClubCatalogs', {
      fetchBooking: 'fetchBeachClubBooking',
      setBookingStatus: 'setBeachClubBookingStatus',
      updateBooking: 'updateBeachClubBooking',
    }),
    // The backend recalculates the quote on every edit; the toast shows the new total
    updatedMessage(result) {
      return `Reserva actualizada. Total a pagar en la entrada: ${this.formatMoney((result.data || {}).total)}`
    },
    cancelSection(booking) {
      if (String(booking.status) !== CANCELLED) return []
      return [{ title: 'Cancelación', fields: [{ label: 'Motivo de cancelación', value: booking.cancelReason, wide: true }] }]
    },
    formatDay: formatXcalacocoDay,
    formatDateTime: formatXcalacocoDateTime,
    formatMoney: formatBeachClubMoney,
    statusVariant: xcalacocoStatusVariant,
  },
}
</script>

<style scoped>
.notes {
  white-space: pre-line;
}
</style>
